// src/components/SMSCampaign/SMSCampaign.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card as UiCard,
  CardContent as UiCardContent,
  Input,
  Button as AcmeButton,
  Dialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
  Select as AcmeSelect,
} from "../ui";
import {
  acme,
  acmeDraftCardSx,
  acmePageSectionCardSx,
  acmeBillingSectionContentSx,
} from "../../theme/acmeTheme";
import PageSnackbars from "../common/PageSnackbars";
import { TextCancelButton } from "../button";
import {
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { usePageSnackbar } from "../common/usePageSnackbar";
import SmsCreditsUsageBar from "../SmsCreditsUsageBar/SmsCreditsUsageBar";
import axios from "axios";
import {
  countSegments,
  encodingLabel,
  segmentCharLimit,
} from "../../utils/smsSegments";
import { canSeeSmsProviderPricing } from "../../utils/userRoles";

const DEFAULT_STOP_FOOTER = "STOP https://msbu.at/xxxxxx";

const formatCredits = (value) => {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return n.toLocaleString();
};

const formatEur = (value) => {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(n);
  } catch {
    return `€${n.toFixed(2)}`;
  }
};

const creditsToEur = (credits, creditValueEur) =>
  formatEur((Number(credits) || 0) * (Number(creditValueEur) || 0));

function SmsPreviewPhone({ sender, body }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 252,
        mx: "auto",
        borderRadius: "28px",
        border: `7px solid ${acme.colors.foreground}`,
        bgcolor: acme.colors.muted,
        boxShadow: acme.shadow.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: 380, md: 440 },
        maxHeight: 480,
      }}
    >
      <Box
        sx={{
          pt: 1.25,
          pb: 0.75,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          bgcolor: acme.colors.background,
          borderBottom: `1px solid ${acme.colors.border}`,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 5,
            borderRadius: acme.radius.full,
            bgcolor: acme.colors.foreground,
            opacity: 0.85,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: acme.colors.foreground,
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
          }}
        >
          {sender}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.5,
          py: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            alignSelf: "flex-start",
            bgcolor: acme.colors.background,
            color: acme.colors.foreground,
            borderRadius: "18px",
            borderTopLeftRadius: 6,
            px: 1.5,
            py: 1.25,
            maxWidth: "92%",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {body}
        </Box>
      </Box>
      <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: 96,
            height: 4,
            borderRadius: acme.radius.full,
            bgcolor: acme.colors.foreground,
            opacity: 0.15,
          }}
        />
      </Box>
    </Box>
  );
}

const hasOptOut = (text) =>
  /\b(stop|unsubscribe|cancel|end|quit|optout)\b/i.test(String(text || "")) ||
  /https?:\/\/\S+\/u\//.test(String(text || "")) ||
  /https?:\/\/msbu\.at\//.test(String(text || ""));

// Mirror of backend toAlphaSenderId: fold diacritics, keep letters/digits/
// spaces, max 11 chars, must contain a letter.
const toSenderPreview = (label) => {
  const folded = String(label || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
  const cleaned = folded
    .replace(/[^A-Za-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 11)
    .trim();
  return /[A-Za-z]/.test(cleaned) ? cleaned : "";
};

const SMSCampaign = () => {
  const { user } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaignName, setCampaignName] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [verifiedPhones, setVerifiedPhones] = useState([]);
  const [pendingPhones, setPendingPhones] = useState([]);
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [contactOption, setContactOption] = useState("contacts");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [manualContacts, setManualContacts] = useState("");
  const [message, setMessage] = useState("");
  const [charCount, setCharCount] = useState(0);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [composeMeta, setComposeMeta] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const skipNextDraftSave = useRef(false);
  const { setSuccessMessage, setErrorMessage, snackbarProps } =
    usePageSnackbar();

  const selectedPhone = verifiedPhones.find(
    (p) => (p.e164 || p.phone) === senderPhone,
  );
  const labelSender = toSenderPreview(selectedPhone?.label);
  const previewSender = labelSender || "Label";
  const stopFooterSample = composeMeta?.stopLinkSample || DEFAULT_STOP_FOOTER;
  const previewBody =
    (message || "Your message will appear here…") +
    (message && !hasOptOut(message) ? `\n${stopFooterSample}` : "");

  const messageWithStop = hasOptOut(message)
    ? message
    : message
      ? `${message}${message.endsWith("\n") ? "" : "\n"}${stopFooterSample}`
      : "";
  const segmentCount = countSegments(messageWithStop);
  const charLimit = segmentCharLimit(messageWithStop || message);
  const encoding = encodingLabel(messageWithStop || message);

  const loadMeta = async () => {
    try {
      const [phonesRes, creditsRes, composeRes] = await Promise.all([
        axios.get("/api/sms-phones"),
        axios.get("/api/sms-campaigns/credits"),
        axios.get("/api/sms-campaigns/compose-meta"),
      ]);
      const verified = (phonesRes.data || []).filter(
        (p) => p.status === "active" && p.providerActivationConfirmedAt,
      );
      const pending = (phonesRes.data || []).filter(
        (p) =>
          p.isVerified &&
          (p.status !== "active" || !p.providerActivationConfirmedAt),
      );
      setVerifiedPhones(verified);
      setPendingPhones(pending);
      setCreditsInfo(creditsRes.data);
      setComposeMeta(composeRes.data);
      setSenderPhone((prev) => {
        if (prev) return prev;
        if (verified.length === 1) return verified[0].e164 || verified[0].phone;
        return prev;
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDraftIntoForm = useCallback(
    (draft) => {
      skipNextDraftSave.current = true;
      setDraftId(draft._id);
      setCampaignName(draft.campaignName || "");
      setSenderPhone(draft.draftSenderPhone || "");
      setMessage(draft.draftMessage || "");
      setContactOption(
        draft.draftRecipientSource === "paste" ? "copy" : "contacts",
      );
      setSelectedGroups(draft.draftRecipientGroupNames || []);
      setManualContacts(draft.draftManualContacts || "");
      setDraftLoaded(true);
      setPendingDraft(null);
      if (searchParams.get("draft")) {
        setSearchParams({});
      }
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get("/api/sms-campaigns/draft");
        if (cancelled || !res.data) return;
        const draft = res.data;
        const draftParam = searchParams.get("draft");
        if (draftParam && String(draft._id) === String(draftParam)) {
          loadDraftIntoForm(draft);
        } else {
          setPendingDraft(draft);
        }
      } catch (err) {
        console.error("Failed to fetch SMS campaign draft:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipNextDraftSave.current) {
      skipNextDraftSave.current = false;
      return undefined;
    }
    if (!campaignName?.trim()) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        let recipients = [];
        if (contactOption === "contacts") {
          recipients = selectedGroups;
        } else {
          recipients = manualContacts
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== "");
        }
        const res = await axios.put("/api/sms-campaigns/draft", {
          draftId: draftId || undefined,
          campaignName,
          senderPhone,
          message,
          contactOption,
          recipients,
          manualContacts,
        });
        setDraftId(res.data._id);
        setDraftLoaded(true);
        setPendingDraft(null);
      } catch (err) {
        console.error("Failed to save SMS campaign draft:", err);
      }
    }, 900);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaignName,
    senderPhone,
    message,
    contactOption,
    selectedGroups,
    manualContacts,
    draftId,
  ]);

  const handleDiscardDraft = async (id) => {
    try {
      await axios.delete(`/api/sms-campaigns/draft/${id}`);
      setPendingDraft(null);
      if (draftId === id) {
        setDraftId(null);
        setDraftLoaded(false);
        setCampaignName("");
        setSenderPhone("");
        setMessage("");
        setManualContacts("");
        setSelectedGroups([]);
        setContactOption("contacts");
      }
      setSuccessMessage("Draft discarded.");
    } catch (err) {
      console.error("Failed to discard SMS draft:", err);
      setErrorMessage("Failed to discard draft.");
    }
  };

  useEffect(() => {
    if (contactOption === "contacts") {
      axios
        .get("/api/sms-contacts/groups")
        .then((response) => setAvailableGroups(response.data))
        .catch((error) => console.error("Error fetching SMS groups", error));
    }
  }, [contactOption]);

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    const withStop = hasOptOut(value)
      ? value
      : value
        ? `${value}${value.endsWith("\n") ? "" : "\n"}${stopFooterSample}`
        : "";
    setCharCount(withStop.length);
  };

  const buildPayload = () => {
    let recipients = [];
    if (contactOption === "contacts") {
      recipients = selectedGroups;
    } else {
      recipients = manualContacts
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "");
    }
    return {
      campaignName,
      senderPhone,
      message,
      contactOption,
      recipients,
      manualContacts,
      draftCampaignId: draftId || undefined,
    };
  };

  const openConfirm = async () => {
    if (!campaignName || !senderPhone || !message) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }
    if (!labelSender) {
      setErrorMessage(
        "Add a company label for this number in SMS Phones first.",
      );
      return;
    }
    const payload = buildPayload();
    if (payload.recipients.length === 0) {
      setErrorMessage("No recipients specified.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post("/api/sms-campaigns/estimate", payload);
      setEstimate(data);
      setEstimateOpen(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Could not estimate campaign.",
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmSend = async () => {
    setLoading(true);
    try {
      await axios.post("/api/sms-campaigns", buildPayload());
      setSuccessMessage("SMS campaign queued and processed.");
      setEstimateOpen(false);
      setEstimate(null);
      setDraftId(null);
      setDraftLoaded(false);
      setPendingDraft(null);
      setCampaignName("");
      setMessage("");
      setManualContacts("");
      setSelectedGroups([]);
      loadMeta();
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Send failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  const lacksCredits =
    creditsInfo &&
    !creditsInfo.unlimitedSms &&
    Math.floor(creditsInfo.smsCredits ?? 0) <= 0;

  const showProviderPricing = canSeeSmsProviderPricing(user?.role);

  const confirmBlocked =
    estimate && !estimate.unlimitedSms && estimate.sufficientFunds === false;

  return (
    <>
      <SmsCreditsUsageBar user={user} creditsInfo={creditsInfo} />
      <PageShell>
        <PageHeader
          title="Create Campaign"
          description="Compose an SMS campaign, review segment pricing, and send to subscribed contacts."
          sx={{ mb: 2 }}
        />
        {pendingDraft && !draftLoaded && (
          <UiCard
            sx={acmeDraftCardSx}
            onClick={() => loadDraftIntoForm(pendingDraft)}
          >
            <UiCardContent
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                py: 2,
                "&:last-child": { pb: 2 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Continue your draft
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: acme.colors.mutedForegroundSolid, mt: 0.5 }}
                >
                  You started &ldquo;{pendingDraft.campaignName}&rdquo; but
                  didn&apos;t finish. Click here to continue where you left off.
                </Typography>
              </Box>
              <AcmeButton
                size="small"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDiscardDraft(pendingDraft._id);
                }}
                sx={{ flexShrink: 0 }}
              >
                Discard
              </AcmeButton>
            </UiCardContent>
          </UiCard>
        )}

        <UiCard sx={{ ...acmePageSectionCardSx, boxShadow: "none", mt: 0 }}>
          <UiCardContent sx={acmeBillingSectionContentSx}>
            <PageBlockTitle sx={{ mb: 1 }}>Create Campaign</PageBlockTitle>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 2.5, md: 3 },
                alignItems: { xs: "stretch", md: "center" },
              }}
            >
              <Box
                component="form"
                noValidate
                autoComplete="off"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.75,
                  flex: 1,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Input
                  label="Campaign name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  required
                />

                <AcmeSelect
                  label="Sender phone"
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                  required
                >
                  {verifiedPhones.length === 0 && (
                    <MenuItem value="" disabled>
                      Add and verify a phone in SMS Phones first
                    </MenuItem>
                  )}
                  {verifiedPhones.map((p) => (
                    <MenuItem key={p._id} value={p.e164 || p.phone}>
                      {p.e164 || p.phone}
                      {p.label ? ` — ${p.label}` : ""}
                    </MenuItem>
                  ))}
                </AcmeSelect>

                {senderPhone &&
                  (labelSender ? (
                    <Alert severity="info" sx={{ mt: -1 }}>
                      Recipients will see &quot;{labelSender}&quot; as the
                      sender.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mt: -1 }}>
                      Add a company label in SMS Phones to send campaigns.
                    </Alert>
                  ))}

                {verifiedPhones.length === 0 && pendingPhones.length > 0 && (
                  <Alert severity="warning">
                    You have {pendingPhones.length} sender
                    {pendingPhones.length === 1 ? "" : "s"} that still need to
                    confirm the 6-digit verification code. Finish the code step
                    in SMS Phones to unlock them for campaigns.
                  </Alert>
                )}

                <AcmeSelect
                  label="Recipients"
                  value={contactOption}
                  onChange={(e) => setContactOption(e.target.value)}
                >
                  <MenuItem value="contacts">Contact groups</MenuItem>
                  <MenuItem value="copy">Paste numbers</MenuItem>
                </AcmeSelect>

                {contactOption === "contacts" ? (
                  <AcmeSelect
                    label="Groups"
                    multiple
                    value={selectedGroups}
                    onChange={(e) => setSelectedGroups(e.target.value)}
                    renderValue={(selected) =>
                      selected
                        .map((groupName) => {
                          const group = availableGroups.find(
                            (g) => g.groupName === groupName,
                          );
                          return group
                            ? `${groupName} (${group.subscribedCount ?? group.totalCount} subscribed)`
                            : groupName;
                        })
                        .join(", ")
                    }
                  >
                    {availableGroups.map((group) => (
                      <MenuItem key={group.groupName} value={group.groupName}>
                        {group.groupName} ({group.subscribedCount ?? 0}{" "}
                        subscribed / {group.totalCount} total)
                      </MenuItem>
                    ))}
                  </AcmeSelect>
                ) : (
                  <Input
                    label="Numbers (comma-separated, include country code)"
                    value={manualContacts}
                    onChange={(e) => setManualContacts(e.target.value)}
                    placeholder="+491701111111, +442222222222"
                  />
                )}

                <Input
                  label="Message"
                  multiline
                  rows={4}
                  value={message}
                  onChange={handleMessageChange}
                  required
                  helperText={
                    composeMeta
                      ? `${encoding}: ${charCount} chars with STOP link · ${segmentCount} segment${segmentCount === 1 ? "" : "s"} (max ${charLimit}/segment)`
                      : "STOP link is added on send and counted in pricing review"
                  }
                />

                <AcmeButton
                  variant="default"
                  onClick={openConfirm}
                  disabled={loading || lacksCredits}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Review pricing & send"
                  )}
                </AcmeButton>
              </Box>

              <Box
                sx={{
                  width: { xs: "100%", md: 252 },
                  flexShrink: 0,
                  position: { md: "sticky" },
                  top: { md: 24 },
                }}
              >
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: acme.colors.mutedForegroundSolid,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Preview
                </Typography>
                <SmsPreviewPhone sender={previewSender} body={previewBody} />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: "block",
                    textAlign: "center",
                    color: acme.colors.mutedForegroundSolid,
                    fontSize: "0.6875rem",
                    lineHeight: 1.4,
                  }}
                >
                  Recipients see your company label as the sender.
                </Typography>
              </Box>
            </Box>
          </UiCardContent>
        </UiCard>

        <Dialog
          open={estimateOpen}
          onClose={() => !loading && setEstimateOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitleText>Confirm SMS campaign</DialogTitleText>
          <DialogBody>
            {estimate && (
              <>
                {confirmBlocked && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {estimate.providerAvailable === false &&
                    estimate.providerUnavailableMessage ? (
                      estimate.providerUnavailableMessage
                    ) : (
                      <>
                        Not enough SMS credits. This campaign needs about{" "}
                        {formatCredits(estimate.creditsRequired)} credits (≈{" "}
                        {estimate.estimatedChargeEur != null
                          ? formatEur(estimate.estimatedChargeEur)
                          : creditsToEur(
                              estimate.creditsRequired,
                              estimate.creditValueEur,
                            )}
                        ), you have {formatCredits(estimate.smsCreditsBalance)}{" "}
                        (≈{" "}
                        {creditsToEur(
                          estimate.smsCreditsBalance,
                          estimate.creditValueEur,
                        )}
                        ). Buy more in Billing → SMS Marketing Plan.
                      </>
                    )}
                  </Alert>
                )}
                <Typography
                  component="div"
                  variant="body2"
                  sx={{ color: acme.colors.mutedForegroundSolid }}
                >
                  <strong>Sender:</strong> {estimate.senderE164}
                  {estimate.senderDisplay ? ` (${estimate.senderDisplay})` : ""}
                  <br />
                  <strong>Recipients:</strong> {estimate.recipientCount}{" "}
                  subscribed numbers
                  <br />
                  <strong>Characters (your text):</strong>{" "}
                  {estimate.messageLength ?? message.length}
                  <br />
                  {estimate.stopLinkAppended !== false && (
                    <>
                      <strong>Characters (with STOP link):</strong>{" "}
                      {estimate.messageLengthWithStopLink}
                      <br />
                    </>
                  )}
                  {!estimate.unlimitedSms && (
                    <>
                      <strong>Estimated cost:</strong>{" "}
                      {estimate.estimatePriceAvailable
                        ? `${formatCredits(estimate.creditsRequired)} credits (≈ ${
                            estimate.estimatedChargeEur != null
                              ? formatEur(estimate.estimatedChargeEur)
                              : creditsToEur(
                                  estimate.creditsRequired,
                                  estimate.creditValueEur,
                                )
                          })`
                        : "unavailable"}
                      <br />
                      <strong>Your SMS credits:</strong>{" "}
                      {formatCredits(estimate.smsCreditsBalance)} (≈{" "}
                      {creditsToEur(
                        estimate.smsCreditsBalance,
                        estimate.creditValueEur,
                      )}
                      )
                      <br />
                    </>
                  )}
                  {showProviderPricing &&
                    estimate.showProviderPricing &&
                    estimate.providerEstimate?.providerSubtotal != null && (
                      <>
                        <strong>BulkGate cost (estimate):</strong>{" "}
                        {formatEur(estimate.providerEstimate.providerSubtotal)}
                        <br />
                      </>
                    )}
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {estimate.compliance?.optInReminder}
                </Alert>
                {estimate.compliance?.stopLinkNote && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {estimate.compliance.stopLinkNote}
                  </Alert>
                )}
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <TextCancelButton
              onClick={() => setEstimateOpen(false)}
              disabled={loading}
            >
              Cancel
            </TextCancelButton>
            <AcmeButton
              variant="default"
              onClick={confirmSend}
              disabled={loading || confirmBlocked}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Confirm send"
              )}
            </AcmeButton>
          </DialogFooter>
        </Dialog>

        <PageSnackbars {...snackbarProps} />
      </PageShell>
    </>
  );
};

export default SMSCampaign;
